// External dependencies
import React, { useEffect, useState } from 'react';

// Internal dependencies
import AdminLayout from '@/modules/admin/client/components/AdminLayout.component';
import CmsAdminNav from '@/modules/cms/client/components/CmsAdminNav.component';
import CmsEditor from '@/modules/cms/client/components/CmsEditor.component';
import {
  createAnnouncement,
  deleteAnnouncement,
  getAnnouncements,
  updateAnnouncement,
} from '@/modules/cms/client/api/admin-cms.api';

const VARIANTS = ['info', 'success', 'warning', 'danger'];

const EMPTY_DRAFT = {
  content: '',
  variant: 'info',
  enabled: false,
  dismissible: true,
  startAt: '',
  endAt: '',
};

/**
 * Is this announcement showing on the site right now?
 *
 * Mirrors `Announcement.activeQuery()` on the server, so the admin list does
 * not claim a scheduled banner is already live.
 *
 * @param {Object} announcement - Announcement document
 * @returns {Boolean}
 */
function isLive(announcement) {
  if (!announcement.enabled) {
    return false;
  }

  const now = new Date();

  if (announcement.startAt && new Date(announcement.startAt) > now) {
    return false;
  }

  if (announcement.endAt && new Date(announcement.endAt) <= now) {
    return false;
  }

  return true;
}

export default function AdminCmsAnnouncements() {
  const [announcements, setAnnouncements] = useState([]);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const load = () => {
    setIsLoading(true);
    setError(null);

    getAnnouncements()
      .then(setAnnouncements)
      .catch(() => setError('Failed to load announcements.'))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const setField = (name, value) => {
    setDraft(previous => ({ ...previous, [name]: value }));
  };

  const handleCreate = async event => {
    event.preventDefault();

    if (!draft.content) {
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await createAnnouncement({
        ...draft,
        startAt: draft.startAt ? new Date(draft.startAt).toISOString() : null,
        endAt: draft.endAt ? new Date(draft.endAt).toISOString() : null,
      });
      setDraft(EMPTY_DRAFT);
      setSuccess('Announcement created.');
      load();
    } catch (err) {
      const message =
        err.response && err.response.data && err.response.data.message;
      setError(message || 'Failed to create announcement.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggle = async announcement => {
    try {
      const saved = await updateAnnouncement(announcement._id, {
        enabled: !announcement.enabled,
      });
      setAnnouncements(previous =>
        previous.map(item => (item._id === saved._id ? saved : item)),
      );
    } catch (err) {
      setError('Failed to change announcement.');
    }
  };

  const handleDelete = async announcement => {
    if (!window.confirm('Delete this announcement? This cannot be undone.')) {
      return;
    }

    try {
      await deleteAnnouncement(announcement._id);
      setAnnouncements(previous =>
        previous.filter(item => item._id !== announcement._id),
      );
    } catch (err) {
      setError('Failed to delete announcement.');
    }
  };

  return (
    <AdminLayout>
      <CmsAdminNav />
      <div className="container">
        <div className="row" style={{ marginBottom: '1rem' }}>
          <div className="col-sm-9">
            <h2>Announcements</h2>
            <p>
              Site-wide banners shown above every page. A banner is live when it
              is enabled and inside its schedule.
            </p>
          </div>
          <div className="col-sm-3" style={{ marginTop: '20px' }}>
            <a href="/admin/cms" className="btn btn-default btn-block">
              Back to pages
            </a>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <form className="panel panel-default" onSubmit={handleCreate}>
          <div className="panel-heading">New announcement</div>
          <div className="panel-body">
            <div className="form-group">
              <label htmlFor="admin-announcement-content">Message</label>
              <CmsEditor
                id="admin-announcement-content"
                value={draft.content}
                placeholder="Write the announcement"
                onChange={value => setField('content', value)}
                compact
              />
            </div>

            <div className="row">
              <div className="col-sm-3">
                <div className="form-group">
                  <label htmlFor="admin-announcement-variant">Style</label>
                  <select
                    id="admin-announcement-variant"
                    className="form-control"
                    value={draft.variant}
                    onChange={event => setField('variant', event.target.value)}
                  >
                    {VARIANTS.map(variant => (
                      <option key={variant} value={variant}>
                        {variant}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-sm-3">
                <div className="form-group">
                  <label htmlFor="admin-announcement-start">Starts</label>
                  <input
                    id="admin-announcement-start"
                    type="datetime-local"
                    className="form-control"
                    value={draft.startAt}
                    onChange={event => setField('startAt', event.target.value)}
                  />
                </div>
              </div>
              <div className="col-sm-3">
                <div className="form-group">
                  <label htmlFor="admin-announcement-end">Ends</label>
                  <input
                    id="admin-announcement-end"
                    type="datetime-local"
                    className="form-control"
                    value={draft.endAt}
                    onChange={event => setField('endAt', event.target.value)}
                  />
                </div>
              </div>
              <div className="col-sm-3" style={{ marginTop: '25px' }}>
                <div className="checkbox">
                  <label htmlFor="admin-announcement-enabled">
                    <input
                      id="admin-announcement-enabled"
                      type="checkbox"
                      checked={draft.enabled}
                      onChange={event =>
                        setField('enabled', event.target.checked)
                      }
                    />
                    Enabled
                  </label>
                </div>
                <div className="checkbox">
                  <label htmlFor="admin-announcement-dismissible">
                    <input
                      id="admin-announcement-dismissible"
                      type="checkbox"
                      checked={draft.dismissible}
                      onChange={event =>
                        setField('dismissible', event.target.checked)
                      }
                    />
                    Dismissible
                  </label>
                </div>
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSaving || !draft.content}
            >
              {isSaving ? 'Saving…' : 'Create announcement'}
            </button>
          </div>
        </form>

        {isLoading ? (
          <p>Loading announcements…</p>
        ) : (
          <table className="table table-striped">
            <thead>
              <tr>
                <th>Message</th>
                <th>Style</th>
                <th>Schedule</th>
                <th>State</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {announcements.map(announcement => (
                <tr key={announcement._id}>
                  <td
                    dangerouslySetInnerHTML={{ __html: announcement.content }}
                  />
                  <td>
                    <span className={`label label-${announcement.variant}`}>
                      {announcement.variant}
                    </span>
                  </td>
                  <td>
                    {announcement.startAt
                      ? new Date(announcement.startAt).toLocaleString()
                      : '—'}
                    {' → '}
                    {announcement.endAt
                      ? new Date(announcement.endAt).toLocaleString()
                      : '—'}
                  </td>
                  <td>
                    {isLive(announcement) ? (
                      <span className="label label-success">Live</span>
                    ) : (
                      <span className="label label-default">
                        {announcement.enabled ? 'Scheduled' : 'Disabled'}
                      </span>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-default btn-xs"
                      style={{ marginRight: '5px' }}
                      onClick={() => handleToggle(announcement)}
                    >
                      {announcement.enabled ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-xs"
                      onClick={() => handleDelete(announcement)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {!announcements.length && (
                <tr>
                  <td colSpan="5">No announcements yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}

AdminCmsAnnouncements.propTypes = {};
